import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import {
  FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw,
  FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed:  { label: 'Failed',  color: '#f87171' },
};

const PAGE_SIZE    = 10;
const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
const getNcToken      = () => localStorage.getItem('amsToken')     || '';

interface NowCertsClient { name: string; databaseId: string; eMail?: string; }

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface InvoiceTransaction {
  transactionId: string;
  fileName: string;
  created: string;
  userName: string;
  status: string;
  responseJson?: string;
  submittedRequestJson?: string;
  selectionOptionsJson?: string;
  isLiked?: boolean | null;
  isUpdateInNowcerts?: boolean;
  totalCount?: number;
}

const getClientName = (tx: InvoiceTransaction): string => {
  try {
    if (!tx.selectionOptionsJson) return '—';
    const p = JSON.parse(tx.selectionOptionsJson);
    return p.clientName || '—';
  } catch { return '—'; }
};

const getPolicyNumber = (tx: InvoiceTransaction): string => {
  try {
    if (!tx.selectionOptionsJson) return '—';
    const p = JSON.parse(tx.selectionOptionsJson);
    return p.policyNumber || '—';
  } catch { return '—'; }
};

/* ════════════════════════════════ */
const InvoiceScanHistory: React.FC = () => {
  const [rows,       setRows]       = useState<InvoiceTransaction[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview */
  const [previewTx,      setPreviewTx]      = useState<InvoiceTransaction | null>(null);
  const [previewPdfUrl,  setPreviewPdfUrl]  = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData,    setPreviewData]    = useState<Record<string, string> | null>(null);
  const [previewLineItems, setPreviewLineItems] = useState<any[]>([]);

  /* Endorsement fee (preview) */
  const [feeLoading, setFeeLoading] = useState<Record<number, boolean>>({});
  const [feeSuccess, setFeeSuccess] = useState<Record<number, boolean>>({});
  const [feeError,   setFeeError]   = useState<Record<number, string>>({});

  /* Upload to AMS */
  const [uploadAmsOpen,           setUploadAmsOpen]           = useState(false);
  const [uploadAmsClientQuery,    setUploadAmsClientQuery]    = useState('');
  const [uploadAmsClientList,     setUploadAmsClientList]     = useState<NowCertsClient[]>([]);
  const [uploadAmsClientLoading,  setUploadAmsClientLoading]  = useState(false);
  const [uploadAmsSelectedClient, setUploadAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [uploadAmsLoading,        setUploadAmsLoading]        = useState(false);
  const [uploadAmsError,          setUploadAmsError]          = useState('');
  const [uploadAmsSuccess,        setUploadAmsSuccess]        = useState(false);

  /* Feedback */
  const [feedbackTxId,       setFeedbackTxId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]       = useState('');
  const [feedbackRating,     setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]     = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError,      setFeedBackError]      = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);
  const [billingDropdownOpen,  setBillingDropdownOpen]  = useState(false);
  const billingDropdownRef = useRef<HTMLDivElement>(null);
  const [historyFieldErrors,   setHistoryFieldErrors]   = useState<{ totalAmountDue?: string; totalGrossAmount?: string; billingTypeAmount?: string; selectedBillingType?: string }>({});

  /* Toast */
  const { showSuccess } = useToast();

  /* Billing dropdown click-outside */
  useEffect(() => {
    if (!billingDropdownOpen) return;
    const handle = (e: MouseEvent) => {
      if (billingDropdownRef.current && !billingDropdownRef.current.contains(e.target as Node))
        setBillingDropdownOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [billingDropdownOpen]);

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIInvoiceScan/history?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToolboxToken()}`, accept: 'application/json' }, signal },
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const data: InvoiceTransaction[] = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(Array.isArray(data) && data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.fileName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      getClientName(r).toLowerCase().includes(q) ||
      getPolicyNumber(r).toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const onPreviewClick = useCallback(async (tx: InvoiceTransaction) => {
    setPreviewTx(tx);
    setPreviewPdfUrl(null);
    setPreviewData(null);
    setHistoryFieldErrors({});
    setPreviewLineItems([]);
    setPreviewLoading(true);

    /* Parse invoice data into editable state */
    try {
      const isUpdated = !!tx.isUpdateInNowcerts;
      const rawJson = isUpdated ? tx.submittedRequestJson : tx.responseJson;
      const parsed = rawJson ? JSON.parse(rawJson) : null;
      const selOpts = tx.selectionOptionsJson ? JSON.parse(tx.selectionOptionsJson) : {};
      if (parsed) {
        setPreviewData({
          invoiceNumber:         parsed.invoiceNumber         || '',
          invoiceDate:           parsed.invoiceDate           || '',
          invoiceDueDate:        parsed.invoiceDueDate        || '',
          totalAmountDue:        parsed.totalAmountDue        || '',
          totalGrossAmount:      parsed.totalGrossAmount      || '',
          insuredName:           parsed.insuredName           || '',
          policyNumber:          parsed.policyNumber || selOpts.policyNumber || '',
          insuranceCompany:      parsed.insuranceCompany      || '',
          totalBrokerCommission: parsed.totalBrokerCommission || '',
          selectedBillingType:   parsed.selectedBillingType   || '',
          billingTypeAmount:     parsed.billingTypeAmount     || '',
        });
        setPreviewLineItems(Array.isArray(parsed.invoiceLineItems) ? parsed.invoiceLineItems : []);
      }
    } catch { /* silent */ }

    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIInvoiceScan/previewdocument`,
        { transactionId: tx.transactionId, fileName: tx.fileName },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      setPreviewPdfUrl(res.data.fileUrl ?? null);
    } catch { /* silent */ }
    finally { setPreviewLoading(false); }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewTx(null);
    setPreviewPdfUrl(null);
    setPreviewData(null);
    setPreviewLineItems([]);
    setFeeLoading({});
    setFeeSuccess({});
    setFeeError({});
  }, []);

  /* ── Endorsement fee from preview ── */
  const isHistoryNumeric = (val: string) => /^-?\d+(\.\d+)?$/.test(val.trim().replace(/[$,]/g, ''));
  const HISTORY_MAX_AMOUNT = 9_999_999.99;
  const fmtHistoryAmountInput = (val: string): string => {
    const stripped = val.replace(/[^0-9.]/g, '');
    const parts = stripped.split('.');
    return parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('').substring(0, 2) : parts[0];
  };

  const validateHistoryFields = (): boolean => {
    const errors: typeof historyFieldErrors = {};
    if (previewData?.totalAmountDue?.trim()) {
      if (!isHistoryNumeric(previewData.totalAmountDue)) errors.totalAmountDue = 'Must be a valid number';
      else if (parseFloat(previewData.totalAmountDue) > HISTORY_MAX_AMOUNT) errors.totalAmountDue = 'Amount cannot exceed $9,999,999.99';
    }
    if (previewData?.totalGrossAmount?.trim()) {
      if (!isHistoryNumeric(previewData.totalGrossAmount)) errors.totalGrossAmount = 'Must be a valid number';
      else if (parseFloat(previewData.totalGrossAmount) > HISTORY_MAX_AMOUNT) errors.totalGrossAmount = 'Amount cannot exceed $9,999,999.99';
    }
    if (previewData?.selectedBillingType && !previewData?.billingTypeAmount?.trim())
      errors.billingTypeAmount = 'Amount is required when a fee/tax type is selected';
    else if (previewData?.billingTypeAmount?.trim() && !isHistoryNumeric(previewData.billingTypeAmount))
      errors.billingTypeAmount = 'Must be a valid number';
    else if (previewData?.billingTypeAmount?.trim() && parseFloat(previewData.billingTypeAmount) > HISTORY_MAX_AMOUNT)
      errors.billingTypeAmount = 'Amount cannot exceed $9,999,999.99';
    if (previewData?.billingTypeAmount?.trim() && !previewData?.selectedBillingType)
      errors.selectedBillingType = 'Fee/Tax type is required when an amount is entered';
    setHistoryFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onAddEndorsementFee = useCallback(async (idx: number) => {
    if (!previewTx) return;
    const item = previewLineItems[idx];
    if (!item) return;
    let selOpts: any = {};
    try { selOpts = previewTx.selectionOptionsJson ? JSON.parse(previewTx.selectionOptionsJson) : {}; } catch { /* */ }

    setFeeLoading(p => ({ ...p, [idx]: true }));
    setFeeError(p => ({ ...p, [idx]: '' }));
    try {
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/insertpolicyendorsementfee`,
        {
          ncToken:          getNcToken(),
          insuredId:        selOpts.insuredClientId || '',
          policyId:         selOpts.selectedPolicyId || '',
          policyNumber:     selOpts.policyNumber || '',
          lineCode:         item.lineCode,
          transactionCode:  item.transactionCodeAfterMapped || item.transactionCodeBeforeMapped,
          effectiveDate:    item.effectiveDate,
          grossAmount:      item.grossAmount,
          brokerCommission: item.brokerCommission,
          lineAmount:       item.lineAmount,
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIInvoiceScan/updatehistory`,
        {
          TransactionId:        previewTx.transactionId,
          submittedRequestJson: JSON.stringify({ invoiceLineItems: previewLineItems }),
          selectionOptionsJson: JSON.stringify(selOpts),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      setFeeSuccess(p => ({ ...p, [idx]: true }));
    } catch (err: any) {
      setFeeError(p => ({ ...p, [idx]: err.response?.data?.errorMessage || 'Error adding fee.' }));
    } finally {
      setFeeLoading(p => ({ ...p, [idx]: false }));
    }
  }, [previewTx, previewLineItems]);

  /* ── Upload to AMS ── */
  useEffect(() => {
    const q = uploadAmsClientQuery;
    if (!q.trim()) { setUploadAmsClientList([]); return; }
    setUploadAmsClientLoading(true);
    const t = setTimeout(() => {
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist`, {
        params: { searchText: q, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then(r => setUploadAmsClientList(r.data || []))
        .finally(() => setUploadAmsClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [uploadAmsClientQuery]);

  const openUploadAms = useCallback(() => {
    setUploadAmsClientQuery('');
    setUploadAmsClientList([]);
    setUploadAmsSelectedClient(null);
    setUploadAmsError('');
    setUploadAmsSuccess(false);
    setUploadAmsOpen(true);
  }, []);

  const onUploadToAMSConfirmed = useCallback(async () => {
    if (!uploadAmsSelectedClient || !previewTx) return;
    setUploadAmsLoading(true);
    setUploadAmsError('');
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/uploadV1`,
        {
          TransactionId: previewTx.transactionId,
          FileName:      previewTx.fileName,
          NCPolicyId:    '',
          NCInsuredId:   uploadAmsSelectedClient.databaseId,
          FileData:      '',
          FolderName:    'Invoice',
          TargetName:    'InvoiceScan',
          NowCertsToken: getNcToken(),
        },
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      if (res.data.success) {
        setUploadAmsSuccess(true);
        setTimeout(() => { setUploadAmsOpen(false); setUploadAmsSuccess(false); }, 1500);
      } else {
        setUploadAmsError(res.data.errorMessage || 'Upload failed.');
      }
    } catch { setUploadAmsError('Error while uploading to AMS.'); }
    finally { setUploadAmsLoading(false); }
  }, [previewTx, uploadAmsSelectedClient]);

  /* ── Feedback ── */
  const openFeedback = useCallback((tx: InvoiceTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(tx.transactionId);
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIInvoiceScan/feedback`,
      { TransactionId: feedbackTxId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).catch(() => {});
  }, [feedbackTxId, rows, feedbackText, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const cur = rows.find(r => r.transactionId === feedbackTxId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: next } : r));
    closeFeedback();
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIInvoiceScan/feedback`,
      { TransactionId: feedbackTxId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).catch(() => {});
  }, [feedbackTxId, rows, feedbackText, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAIInvoiceScan/feedback`,
      { TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => setFeedbackSubmitting(false));
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>Invoice Scan History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiFileText size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching invoice scan records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiFileText size={28} />
              <p>{search ? 'No records match your search' : 'No invoice scans yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">File Name</th>
                    <th className="qp-table__th">Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th">Client Name</th>
                    <th className="qp-table__th">Policy #</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Update In AMS</th>
                    <th className="qp-table__th" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Status</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tx, i) => {
                    const isSuccess = tx.status === 'Success';
                    return (
                      <tr key={tx.transactionId ?? i} className="qp-table__row">
                        <td className="qp-table__td" style={{ color: 'var(--accent-purple-light)', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tx.fileName}
                        </td>
                        <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(tx.created ?? '')}</td>
                        <td className="qp-table__td">{tx.userName}</td>
                        <td className="qp-table__td">{getClientName(tx)}</td>
                        <td className="qp-table__td">{getPolicyNumber(tx)}</td>
                        <td className="qp-table__td" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                            whiteSpace: 'nowrap',
                            background: tx.isUpdateInNowcerts ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                            color:      tx.isUpdateInNowcerts ? '#10b981' : '#ef4444',
                          }}>
                            {tx.isUpdateInNowcerts ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>
                          <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[tx.status] || STATUS_CONFIG.Failed).color }}>
                            {(STATUS_CONFIG[tx.status] || { label: tx.status }).label}
                          </span>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <div className="qp-table__actions">
                            {isSuccess ? (
                              <>
                                <button className="qp-table__action-btn" title="Preview" onClick={() => onPreviewClick(tx)}>
                                  <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                                </button>
                              </>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Not Available</span>
                            )}
                          </div>
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <button className="qp-table__action-btn" title="Feedback" onClick={e => openFeedback(tx, e)} style={{ margin: '0 auto' }}>
                            <FiMessageSquare size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback Popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUpClick} title="Thumbs up">
              <FiThumbsUp size={20} />
            </button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDownClick} title="Thumbs down">
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedbackClick}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* ── Upload to AMS Dialog ── */}
      {uploadAmsOpen && ReactDOM.createPortal(
        <div className="csams-overlay" onClick={() => setUploadAmsOpen(false)}>
          <div className="csams-modal" onClick={e => e.stopPropagation()}>
            <div className="csams-modal__header">
              <h3 className="csams-modal__title">Upload In AMS</h3>
              <button className="csams-modal__close" onClick={() => setUploadAmsOpen(false)}>✕</button>
            </div>
            <div className="csams-modal__body">
              <label className="csams-label">Search Client</label>
              <div className="csams-search-wrap">
                <input
                  className="csams-search-input"
                  placeholder="Enter client name..."
                  value={uploadAmsClientQuery}
                  onChange={e => { setUploadAmsClientQuery(e.target.value); setUploadAmsSelectedClient(null); }}
                  autoFocus
                />
                {uploadAmsClientLoading && <span className="csams-search-spinner" />}
                {uploadAmsClientList.length > 0 && !uploadAmsSelectedClient && (
                  <ul className="csams-dropdown">
                    {uploadAmsClientList.map(c => (
                      <li
                        key={c.databaseId}
                        className="csams-dropdown__item"
                        onClick={() => { setUploadAmsSelectedClient(c); setUploadAmsClientQuery(c.name); setUploadAmsClientList([]); }}
                      >
                        {c.name}
                        {c.eMail && <span className="csams-dropdown__email">{c.eMail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {uploadAmsSuccess && <p className="csams-success">Uploaded to AMS successfully!</p>}
              {uploadAmsError   && <p className="csams-error">{uploadAmsError}</p>}
            </div>
            <div className="csams-modal__footer">
              <button className="csams-btn csams-btn--cancel" onClick={() => setUploadAmsOpen(false)}>Cancel</button>
              <button
                className="csams-btn csams-btn--upload"
                disabled={!uploadAmsSelectedClient || uploadAmsLoading}
                onClick={onUploadToAMSConfirmed}
              >
                {uploadAmsLoading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview Modal ── */}
      {previewTx && ReactDOM.createPortal(
        <div
          className="csams-overlay"
          onClick={closePreview}
          style={{ zIndex: 1100, background: 'rgba(0,0,0,0.55)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '92vw', maxWidth: 1260, height: '90vh',
              display: 'flex', flexDirection: 'column',
              borderRadius: 12, background: 'var(--bg-primary)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden', border: '1px solid var(--border-glass)',
            }}
          >
            {/* Preview header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 20px', borderBottom: '1px solid var(--border-glass)',
              flexShrink: 0, background: 'var(--surface-glass)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                  Client: <strong style={{ color: 'var(--accent-purple)' }}>{getClientName(previewTx)}</strong>
                  <span style={{ color: 'var(--text-secondary)', margin: '0 8px' }}>·</span>
                  Policy: <strong>{getPolicyNumber(previewTx)}</strong>
                </span>
                {previewTx.isUpdateInNowcerts && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                    background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
                    color: '#10b981',
                  }}>
                    ✓ Updated in AMS
                  </span>
                )}
              </div>
              <button
                onClick={closePreview}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1,
                  padding: '4px 8px', borderRadius: 6,
                }}
              >✕</button>
            </div>

            {/* Preview body */}
            <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
              {/* Left: PDF */}
              <div className="cspr-pdf-panel">
                <div className="cspr-pdf-panel__header">
                  <span className="cspr-pdf-panel__title">Invoice Document</span>
                  <motion.button
                    className="cspr-action-btn cspr-action-btn--purple"
                    onClick={openUploadAms}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{ fontSize: 12, padding: '5px 14px' }}
                  >
                    Upload In AMS
                  </motion.button>
                </div>
                <div className="cspr-pdf-viewer">
                  {previewPdfUrl
                    ? <iframe src={`${previewPdfUrl}#zoom=90`} title="Invoice Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
                    : previewLoading
                      ? <div className="cspr-pdf-placeholder">Loading document…</div>
                      : <div className="cspr-pdf-placeholder">Preview not available</div>
                  }
                </div>
              </div>

              {/* Right: extracted data */}
              <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '18px 20px', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!previewData ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No extracted data available.</p>
                ) : (() => {
                  const isUpdated = !!previewTx.isUpdateInNowcerts;

                  const BILLING_TYPE_DATA = [
                    { name: 'Initial_Base_Premium', value: 0 }, { name: 'Positive_Endorsement', value: 1 },
                    { name: 'Negative_Endorsement', value: 2 }, { name: 'Cancel', value: 5 },
                    { name: 'Renew', value: 6 }, { name: 'Reinstate', value: 7 },
                    { name: 'Govt_Discount', value: 9 }, { name: 'Premium_Audit', value: 13 },
                    { name: 'General_Discount', value: 14 }, { name: 'Credit', value: 15 },
                    { name: 'Installment', value: 200 }, { name: 'New_Policy_Premium', value: 210 },
                    { name: 'No_Premium_Impact', value: 230 }, { name: 'Fees', value: 3 },
                    { name: 'Agency_Fee', value: 10 }, { name: 'Risk_Management_Fee', value: 101 },
                    { name: 'Carrier_Fee', value: 11 }, { name: 'Policy_Fee', value: 111 },
                    { name: 'MGA_Fee', value: 12 }, { name: 'Inspection_Fee', value: 20 },
                    { name: 'Al_Endorsements_Fee', value: 21 }, { name: 'Subscription_Fee', value: 16 },
                    { name: 'Program_Fee', value: 19 }, { name: 'Taxes', value: 4 },
                    { name: 'State_Tax', value: 17 }, { name: 'Surplus_Lines_Tax', value: 22 },
                    { name: 'Miscellaneous_Agency_Revenue', value: 23 }, { name: 'Other_Tax', value: 18 },
                    { name: 'Fire_Marshall_Tax', value: 181 }, { name: 'Processing_Fee', value: 35 },
                    { name: 'Filing_Fee', value: 351 }, { name: 'APTC_Tax_Credit', value: 45 },
                    { name: 'Expense_Constant', value: 100 }, { name: 'Surplus_Contribution', value: 150 },
                    { name: 'AB_TPA_Fee', value: 160 }, { name: 'Independent_Agent_Broker_Fee', value: 170 },
                    { name: 'Payment_Processing_Fee', value: 180 }, { name: 'Collateral_Fee', value: 190 },
                    { name: 'MVR_Fee', value: 240 }, { name: 'CC_Fee', value: 250 },
                    { name: 'Admin_Fee', value: 350 }, { name: 'Other', value: 8 },
                  ];

                  const controlStyle = (editable: boolean): React.CSSProperties => ({
                    width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 13,
                    background: editable ? 'var(--surface-glass)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${editable ? 'var(--border-glass)' : 'rgba(255,255,255,0.06)'}`,
                    color: editable ? 'var(--text-primary)' : 'var(--text-secondary)',
                    outline: 'none', boxSizing: 'border-box' as const,
                    cursor: editable ? 'auto' : 'default',
                  });

                  const toDateInput = (val: string): string => {
                    if (!val) return '';
                    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
                    const dmy = val.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
                    const mdy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                    if (mdy) return `${mdy[3]}-${mdy[1].padStart(2,'0')}-${mdy[2].padStart(2,'0')}`;
                    return '';
                  };

                  const field = (label: string, key: string, type: 'text' | 'date' | 'select' = 'text', placeholder = '') => (
                    <div key={key}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5, letterSpacing: '0.01em' }}>
                        {label}
                      </div>
                      {type === 'select' ? (
                        <div ref={billingDropdownRef} style={{ position: 'relative' }}>
                          <button
                            type="button"
                            disabled={isUpdated}
                            onClick={() => !isUpdated && setBillingDropdownOpen(o => !o)}
                            style={{ ...controlStyle(!isUpdated), cursor: isUpdated ? 'default' : 'pointer', opacity: isUpdated ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left' }}
                          >
                            <span style={{ color: (previewData[key] ?? '') ? 'var(--text-primary)' : 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {(previewData[key] ?? '') ? (previewData[key] ?? '').replace(/_/g, ' ') : 'Select fee / tax type'}
                            </span>
                            <span style={{ color: 'var(--text-secondary)', fontSize: 10, flexShrink: 0, marginLeft: 4 }}>▾</span>
                          </button>
                          {billingDropdownOpen && (
                            <div className="ncai-billing-dropdown">
                              <div
                                className={`ncai-billing-option ncai-billing-option--placeholder${!(previewData[key] ?? '') ? ' ncai-billing-option--selected' : ''}`}
                                onMouseDown={() => { setPreviewData(prev => prev ? { ...prev, [key]: '' } : prev); setBillingDropdownOpen(false); setHistoryFieldErrors(prev => ({ ...prev, billingTypeAmount: undefined })); }}
                              >Select fee / tax type</div>
                              {BILLING_TYPE_DATA.map(bt => (
                                <div
                                  key={bt.value}
                                  className={`ncai-billing-option${(previewData[key] ?? '') === bt.name ? ' ncai-billing-option--selected' : ''}`}
                                  onMouseDown={() => { setPreviewData(prev => prev ? { ...prev, [key]: bt.name } : prev); setBillingDropdownOpen(false); setHistoryFieldErrors(prev => ({ ...prev, selectedBillingType: undefined })); }}
                                >{bt.name.replace(/_/g, ' ')}</div>
                              ))}
                            </div>
                          )}
                          {historyFieldErrors.selectedBillingType && (
                            <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>{historyFieldErrors.selectedBillingType}</span>
                          )}
                        </div>
                      ) : (
                        <>
                          <input
                            type={type}
                            value={type === 'date' ? toDateInput(previewData[key] ?? '') : (['totalAmountDue', 'totalGrossAmount'].includes(key) ? (previewData[key] ?? '').replace(/[$,]/g, '') : (previewData[key] ?? ''))}
                            readOnly={isUpdated}
                            onChange={e => {
                              if (!isUpdated) {
                                const numericKeys = ['totalAmountDue', 'totalGrossAmount', 'billingTypeAmount'];
                                const v = numericKeys.includes(key) ? fmtHistoryAmountInput(e.target.value) : e.target.value;
                                setPreviewData(prev => prev ? { ...prev, [key]: v } : prev);
                                setHistoryFieldErrors(prev => ({ ...prev, [key]: undefined, ...(key === 'billingTypeAmount' ? { selectedBillingType: undefined } : {}) }));
                              }
                            }}
                            placeholder={placeholder || undefined}
                            style={{ ...controlStyle(!isUpdated), ...((historyFieldErrors as any)[key] ? { borderColor: '#ef4444' } : {}) }}
                          />
                          {(historyFieldErrors as any)[key] && (
                            <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>
                              {(historyFieldErrors as any)[key]}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  );

                  type FieldTuple = [string, string, 'text' | 'date' | 'select', string?];
                  const headerRows: FieldTuple[][] = [
                    [
                      ['Invoice Number',   'invoiceNumber',   'text', 'Enter Invoice Number'],
                      ['Invoice Date',     'invoiceDate',     'date'],
                      ['Invoice Due Date', 'invoiceDueDate',  'date'],
                    ],
                    [
                      ['Total Amount Due ($)',   'totalAmountDue',   'text', 'Enter Total Amount Due'],
                      ['Total Gross Amount ($)', 'totalGrossAmount', 'text', 'Enter Total Gross Amount'],
                      ['Insured Name',       'insuredName',      'text', 'Enter Insured Name'],
                    ],
                    [
                      ['Policy Number',         'policyNumber',        'text',   'Enter Policy Number'],
                      ['Additional Fee/Tax Type','selectedBillingType', 'select'],
                      ['Amount',                'billingTypeAmount',    'text',   'Enter Amount'],
                    ],
                  ];

                  return (
                    <>
                      {/* Header fields */}
                      <div style={{
                        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                        borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14,
                      }}>
                        {headerRows.map((row, ri) => (
                          <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0 14px' }}>
                            {row.map(([label, key, type, ph]) => key ? field(label, key, type, ph) : <div key="empty" />)}
                          </div>
                        ))}
                      </div>

                      {/* Line items table */}
                      {previewLineItems.length > 0 && (
                        <div style={{
                          background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                          borderRadius: 10, overflow: 'hidden',
                        }}>
                          <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-glass)' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                              Line Items ({previewLineItems.length})
                            </span>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid var(--border-glass)' }}>
                                  {['Line Code', 'Transaction Code', 'Billing Type', 'Effective Date', 'Gross Amount', 'Broker Commission', 'Line Amount'].map(col => (
                                    <th key={col} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: 11, letterSpacing: '0.02em' }}>
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {previewLineItems.map((item: any, i: number) => (
                                  <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                    <td style={{ padding: '9px 12px', color: 'var(--accent-purple)', fontWeight: 500, whiteSpace: 'nowrap' }}>{item.lineCode || '—'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-primary)', maxWidth: 180, wordBreak: 'break-word' }}>{item.transactionCodeBeforeMapped || '—'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-secondary)' }}>{item.transactionCodeAfterMapped || 'Other'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{item.effectiveDate || '—'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{item.grossAmount || '—'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{item.brokerCommission || '—'}</td>
                                    <td style={{ padding: '9px 12px', color: 'var(--accent-purple)', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.lineAmount || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                        </div>
                      )}

                      {/* Add Endorsement Fees — outside & below the line items card, right-aligned */}
                      {!isUpdated && previewLineItems.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, paddingTop: 4 }}>
                          {Object.values(feeError).some(Boolean) && (
                            <span style={{ fontSize: 11, color: '#ef4444' }}>Some fees failed. Please try again.</span>
                          )}
                          {Object.keys(feeSuccess).length > 0 && Object.keys(feeSuccess).length === previewLineItems.length ? (
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                              ✓ All endorsement fees added
                            </span>
                          ) : (
                            <motion.button
                              className="cspr-action-btn cspr-action-btn--purple"
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              disabled={Object.values(feeLoading).some(Boolean)}
                              onClick={() => { if (validateHistoryFields()) previewLineItems.forEach((_, i) => onAddEndorsementFee(i)); }}
                              style={{ fontSize: 12, padding: '8px 22px', opacity: Object.values(feeLoading).some(Boolean) ? 0.6 : 1 }}
                            >
                              {Object.values(feeLoading).some(Boolean) ? 'Adding…' : 'Add Endorsement Fees'}
                            </motion.button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default InvoiceScanHistory;

